"use client";

import React, { Suspense, useCallback, useMemo, useRef } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@mui/material";
import { createDynamicTheme } from "./theme";
import { useThemeStore } from "../stores/theme.store";
import { FeedBackProvider } from "../providers/FeedBackProvider/FeedBackProvider";
import PDFGeneratorProvider from "../providers/PDFGeneratorProvider/PDFGeneratorProvider";
import XlsGeneratorProvider from "../providers/XlsGeneratorProvider/XlsGeneratorProvider";
import { RegisterBeneficiaryModalProvider } from "../components/modals/RegisterBeneficiary/RegisterBeneficiaryModalProvider";
import Spinner from "../components/Spinner";
import { instanceBo } from "../utils/axios.create";

function Providers({ children }: React.PropsWithChildren) {
	const [client] = React.useState(new QueryClient());
	// eslint-disable-next-line @typescript-eslint/no-var-requires
	const { QlikEmbedConfig } = require("@qlik/embed-react");

	const tokenRef = useRef(null);

	const getAccessToken = useCallback(async () => {
		if (tokenRef.current) {
			return tokenRef.current;
		}
		try {
			// SIMS-6661: repointed v1 axios POST -> v2 instanceBo POST. The v2 Qlik
			// endpoint (QlikController, consuming the ported QlikService) returns a
			// BaseResponse, so the body.qlikToken access is unchanged from v1.
			const response = await instanceBo.post(
				"/qlik-token?email=dummy_email@email.com",
				{},
			);
			tokenRef.current = response.data.body.qlikToken;
			return tokenRef.current;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error fetching access token", error);
			throw error;
		}
	}, []); // no dependency on token

	const {
		primaryColor,
		secondaryColor,
		surfaceColor,
		surfaceLight,
		surfaceDark,
		primaryDark,
		secondaryLight,
		outlineMain,
		outlineDark,
		textMainDark2,
		textMainMain2,
		textSecondaryMain,
		tertiaryColor,
		surfaceTint,
		successLight,
		successDark,
		brandFaint,
		dividerColor,
		surfaceTwo,
		textPrimaryMain,
		fontFamily,
		isLoaded,
		_hasHydrated,
	} = useThemeStore();
	const theme = useMemo(
		() =>
			createDynamicTheme(
				primaryColor || secondaryColor
					? {
							primaryColor,
							secondaryColor,
							surfaceColor,
							surfaceLight,
							surfaceDark,
							primaryDark,
							secondaryLight,
							outlineMain,
							outlineDark,
							textMainDark2,
							textMainMain2,
							textSecondaryMain,
							tertiaryColor,
							surfaceTint,
							successLight,
							successDark,
							brandFaint,
							dividerColor,
							surfaceTwo,
							textPrimaryMain,
							fontFamily,
						}
					: null,
			),
		[
			primaryColor,
			secondaryColor,
			surfaceColor,
			surfaceLight,
			surfaceDark,
			primaryDark,
			secondaryLight,
			outlineMain,
			outlineDark,
			textMainDark2,
			textMainMain2,
			textSecondaryMain,
			tertiaryColor,
			surfaceTint,
			successLight,
			successDark,
			brandFaint,
			dividerColor,
			surfaceTwo,
			textPrimaryMain,
			fontFamily,
		],
	);

	// Block render until theme store has hydrated from localStorage.
	// If hydrated with cached colors (isLoaded=true), render immediately.
	// If no cached colors, show spinner until settings API loads them.
	if (!_hasHydrated || (!isLoaded && !primaryColor)) {
		return (
			<div
				style={{
					display: "flex",
					width: "100vw",
					height: "100vh",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Spinner />
			</div>
		);
	}

	const hostConfig = {
		host: "https://cmi.us.qlikcloud.com",
		authType: "oauth2",
		clientId: process.env.REACT_APP_QLIK_CLIENT_ID || "",
		noCache: true,
		getAccessToken: getAccessToken,
	};

	return (
		<QlikEmbedConfig.Provider value={hostConfig}>
			<QueryClientProvider client={client}>
				<ThemeProvider theme={theme}>
					<FeedBackProvider>
						<PDFGeneratorProvider>
							<XlsGeneratorProvider>
								<RegisterBeneficiaryModalProvider>
									<Suspense fallback={<Spinner />}>{children}</Suspense>
								</RegisterBeneficiaryModalProvider>
							</XlsGeneratorProvider>
						</PDFGeneratorProvider>
					</FeedBackProvider>
				</ThemeProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</QlikEmbedConfig.Provider>
	);
}

export default Providers;
